import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { apiRequest, fetchApiFileUrl } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable, ZoomableImage } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';
import { unitLabel } from '../../../../shared/contracts/unit-label.js';
import { findJobQuantityByAssignment } from '../../api/jobQuantityApi.js';
import { JobQuantityPanel } from '../billing/JobQuantityPanel.js';
import {
  areaCapturedRows,
  capturedLabel,
  fieldWorkRows,
  fixText,
  gpsReadingFine,
  gpsReadingNotes,
  jobRecordPath,
  jobRecordRows,
  metresText,
  photoLinkPath,
  signedLinkPath,
  sitePosition,
  type JobRecord,
  type Submission,
  type SubmissionGps,
  type SubmissionPhoto,
} from '../../../../shared/contracts/review-submission.js';

export type { Submission, SubmissionGps, SubmissionPhoto };

/**
 * The painter's submission as every reviewer reads it (owner, 25 Oct): one
 * set of panels, used by the review screen every reviewer opens from the BO
 * Queue and by the vendor's dedicated validation screens, so the same fact is
 * never shown two ways. Everything here comes from the system; the only thing
 * a reviewer writes is the job's quantity, at his own level. What each panel
 * says is read in shared/contracts/review-submission, which the phone's review
 * screen reads too; this file is the web's markup for it.
 */

const DL = { display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '4px 16px', fontSize: 13, margin: 0 } as const;
const DT = { fontWeight: 600 } as const;
const H2 = { fontSize: 16, margin: '0 0 8px' } as const;
const SECTION = { marginBottom: 24 } as const;
const MUTED = { color: 'var(--color-text-muted)', fontSize: 13 } as const;

// ── The job, its dealer and site (M11 job record, GET /v1/closure/job-record/:job) ──

export function JobRecordPanel({ jobRequestId }: { readonly jobRequestId: string }): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { decimal, money } = useBusinessNumber();
  const { day } = useBusinessDay();
  const [rec, setRec] = useState<JobRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<JobRecord | null>(jobRecordPath(jobRequestId), { token })
      .then((r) => { if (alive) setRec(r ?? {}); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [jobRequestId, token]);

  const rows = rec ? jobRecordRows(rec, { t, day, decimal, money }) : [];

  return (
    <section aria-label="job-record" style={SECTION}>
      <h2 style={H2}>{t('quality.job.title')}</h2>
      {error ? <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>
        : !rec ? <p style={MUTED}>{t('common.loading')}</p>
        : (
          <dl style={DL} data-testid="job-record-fields">
            {rows.map(([label, value]) => (
              <Fragment key={label}><dt style={DT}>{label}</dt><dd style={{ margin: 0 }}>{value}</dd></Fragment>
            ))}
          </dl>
        )}
    </section>
  );
}

// ── The field work: start, progress, material, issues, completion ──────────

export function FieldWorkPanel({ submission }: { readonly submission: Submission }): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const { progress, materials, issues } = submission;
  return (
    <section aria-label="field-work" style={SECTION}>
      <h2 style={H2}>{t('quality.work.title')}</h2>
      <dl style={DL} data-testid="field-work-fields">
        {fieldWorkRows(submission, { t, at, decimal }).map(([label, value]) => (
          <Fragment key={label}><dt style={DT}>{label}</dt><dd style={{ margin: 0 }}>{value}</dd></Fragment>
        ))}
      </dl>

      <h3 style={{ fontSize: 14, margin: '16px 0 6px' }}>{t('quality.work.progress')}</h3>
      {progress.length === 0 ? <p style={MUTED}>{t('quality.work.none')}</p> : (
        <DataTable
          testId="progress-table"
          rowKey={(r) => r.reportedAt}
          columns={[
            { key: 'at', header: t('quality.photo.col.at'), render: (r) => at(r.reportedAt) },
            { key: 'pct', header: t('quality.work.pct'), render: (r) => `${r.pctComplete}%` },
            { key: 'remarks', header: t('quality.work.remarks'), render: (r) => r.remarks ?? '—' },
          ]}
          rows={progress}
        />
      )}

      <h3 style={{ fontSize: 14, margin: '16px 0 6px' }}>{t('quality.work.materials')}</h3>
      {materials.length === 0 ? <p style={MUTED}>{t('quality.work.none')}</p> : (
        <DataTable
          testId="material-table"
          rowKey={(r) => `${r.materialCode}-${r.capturedAt}`}
          columns={[
            { key: 'code', header: t('quality.work.material'), render: (r) => r.materialCode },
            { key: 'qty', header: t('quality.work.quantity'), render: (r) => `${decimal(r.quantity)} ${unitLabel(r.uomCode ?? '')}`.trim() },
            { key: 'remarks', header: t('quality.work.remarks'), render: (r) => r.remarks ?? '—' },
          ]}
          rows={materials}
        />
      )}

      <h3 style={{ fontSize: 14, margin: '16px 0 6px' }}>{t('quality.work.issues')}</h3>
      {issues.length === 0 ? <p style={MUTED}>{t('quality.work.none')}</p> : (
        <DataTable
          testId="issue-table"
          rowKey={(r) => `${r.reasonCode}-${r.reportedAt}`}
          columns={[
            { key: 'at', header: t('quality.photo.col.at'), render: (r) => at(r.reportedAt) },
            { key: 'reason', header: t('quality.work.reason'), render: (r) => r.reasonCode },
            { key: 'status', header: t('quality.common.status'), render: (r) => r.status },
            { key: 'remarks', header: t('quality.work.remarks'), render: (r) => r.remarks ?? '—' },
            { key: 'response', header: t('quality.work.vendorResponse'), render: (r) => r.vendorResponse ?? '—' },
          ]}
          rows={issues}
        />
      )}
    </section>
  );
}

// ── Photos with the viewer beside them (GET .../photos) ─────────────────────

export function SubmissionPhotos({
  rows,
  onView,
  children,
}: {
  readonly rows: readonly SubmissionPhoto[];
  /** Told which photo is being looked at, e.g. to decide on that one. */
  readonly onView?: (p: SubmissionPhoto) => void;
  /** Shown above the list, on the left of the viewer. */
  readonly children?: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { at } = useBusinessDay();
  const { decimal } = useBusinessNumber();
  const photoLabel = (p: SubmissionPhoto): string => capturedLabel(p.photoType, p.capturedAt, at);

  // The painter's photo is served by M09: it signs a link to the stored file,
  // and the file is fetched with the login like every other API file, then
  // shown in the viewer beside the list.
  const [viewing, setViewing] = useState<SubmissionPhoto | null>(null);
  const [viewUrl, setViewUrl] = useState<string | null>(null);
  const [viewError, setViewError] = useState<string | null>(null);
  const viewUrlRef = useRef<string | null>(null);
  const showUrl = useCallback((url: string | null) => {
    if (viewUrlRef.current) URL.revokeObjectURL(viewUrlRef.current);
    viewUrlRef.current = url;
    setViewUrl(url);
  }, []);
  useEffect(() => () => { if (viewUrlRef.current) URL.revokeObjectURL(viewUrlRef.current); }, []);

  const viewPhoto = useCallback(async (p: SubmissionPhoto) => {
    setViewing(p);
    onView?.(p);
    setViewError(null);
    showUrl(null);
    try {
      const { signedUrl } = await apiRequest<{ signedUrl: string }>(photoLinkPath(p.photoId), { token });
      showUrl(await fetchApiFileUrl(signedLinkPath(signedUrl), token));
    } catch (e) { setViewError((e as Error).message); }
  }, [token, showUrl, onView]);

  return (
    <div className="tl-viewer-split">
      <div className="tl-viewer-split__grid">
        <div>
          {children}
          <h2 style={H2}>{t('quality.photo.submitted')}</h2>
          {rows.length === 0 ? (
            <p data-testid="pv-empty" style={MUTED}>{t('quality.photo.empty')}</p>
          ) : (
            <DataTable<SubmissionPhoto>
              testId="pv-table"
              rowKey={(r) => r.photoId}
              rowTestId={(r) => `pv-row-${r.photoId}`}
              columns={[
                { key: 'photo', header: t('quality.photo.col.photo'), render: (r) => photoLabel(r) },
                { key: 'open', header: '', render: (r) => (
                  <Button type="button" variant={viewing?.photoId === r.photoId ? 'primary' : 'secondary'}
                          data-testid={`pv-open-${r.photoId}`} aria-pressed={viewing?.photoId === r.photoId}
                          onClick={() => void viewPhoto(r)}>
                    {t('quality.photo.open')}
                  </Button>
                ) },
                { key: 'gps', header: t('quality.photo.col.gps'), render: (r) => fixText(r.latitude, r.longitude, r.accuracyM, decimal) },
                { key: 'device', header: t('quality.photo.col.device'), render: (r) => [r.deviceId, r.appVersion].filter(Boolean).join(' · ') || '—' },
                { key: 'decision', header: t('quality.common.decision'), render: (r) => r.decision ?? '—' },
                { key: 'reason', header: t('quality.photo.col.reason'), render: (r) => r.rejectReasonCode ?? '—' },
                { key: 'decidedAt', header: t('quality.photo.col.at'), render: (r) => (r.decisionAt ? at(r.decisionAt) : '—') },
              ]}
              rows={rows}
            />
          )}
        </div>

        <aside className="tl-viewer" aria-label="photo-viewer" data-testid="pv-viewer">
          <div className="tl-viewer__head">
            <h2 className="tl-viewer__title">{viewing ? viewing.photoType : t('quality.photo.viewer')}</h2>
            {viewing && (
              <Button type="button" variant="secondary" data-testid="pv-viewer-close"
                      onClick={() => { setViewing(null); setViewError(null); showUrl(null); }}>
                {t('quality.photo.close')}
              </Button>
            )}
          </div>
          {viewing && (
            <span className="tl-viewer__meta">
              {at(viewing.capturedAt)} · {sitePosition(viewing.latitude, viewing.longitude)}
              {viewing.decision ? ` · ${viewing.decision}` : ''}
            </span>
          )}
          <div className="tl-viewer__frame">
            {!viewing && <p className="tl-viewer__empty" data-testid="pv-viewer-empty">{t('quality.photo.viewerHint')}</p>}
            {viewing && viewError && <p role="alert" className="tl-viewer__empty" style={{ color: 'var(--color-danger)' }}>{viewError}</p>}
            {viewing && !viewError && !viewUrl && <p className="tl-viewer__empty">{t('quality.photo.loading')}</p>}
            {viewing && viewUrl && <ZoomableImage src={viewUrl} alt={photoLabel(viewing)} testId="pv-viewer-img" />}
          </div>
        </aside>
      </div>
    </div>
  );
}

// ── GPS readings against the site (GET .../gps) ──────────────────────────────

export function GpsReadingsTable({ rows }: { readonly rows: readonly SubmissionGps[] }): ReactNode {
  const { t } = useTranslation();
  const { at } = useBusinessDay();
  const readingLabel = (g: SubmissionGps): string => capturedLabel(g.source, g.capturedAt, at);

  const check = (g: SubmissionGps): ReactNode => (
    <span data-testid={`gps-check-${g.gpsId}`} style={{ color: gpsReadingFine(g) ? 'var(--color-success)' : 'var(--color-danger)' }}>
      {gpsReadingNotes(g, t).join(' · ')}
    </span>
  );

  return rows.length === 0 ? (
    <p data-testid="gps-empty" style={MUTED}>{t('quality.gps.empty')}</p>
  ) : (
    <DataTable<SubmissionGps>
      testId="gps-table"
      rowKey={(r) => r.gpsId}
      rowTestId={(r) => `gps-row-${r.gpsId}`}
      columns={[
        { key: 'reading', header: t('quality.gps.col.reading'), render: (r) => readingLabel(r) },
        { key: 'painter', header: t('quality.gps.col.painter'), render: (r) => sitePosition(r.execLatitude, r.execLongitude) },
        { key: 'site', header: t('quality.gps.col.site'), render: (r) => sitePosition(r.siteLatitude, r.siteLongitude) },
        { key: 'distance', header: t('quality.gps.col.distance'), render: (r) => metresText(r.distanceM) },
        { key: 'accuracy', header: t('quality.gps.col.accuracy'), render: (r) => metresText(r.accuracyM) },
        { key: 'check', header: t('quality.gps.col.check'), render: check },
        { key: 'decision', header: t('quality.common.decision'), render: (r) => r.decision ?? '—' },
        { key: 'at', header: t('quality.photo.col.at'), render: (r) => (r.decisionAt ? at(r.decisionAt) : '—') },
      ]}
      rows={rows}
    />
  );
}

// ── The area: as the field force measured it, and the job's quantity ────────

export function AreaPanel({ submission }: { readonly submission: Submission }): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { at } = useBusinessDay();
  const { decimal, number } = useBusinessNumber();
  const fetchRow = useCallback(
    () => findJobQuantityByAssignment(token ?? '', submission.vendorAssignmentId),
    [token, submission.vendorAssignmentId],
  );

  const captured = areaCapturedRows(submission, { t, at, decimal, number });
  return (
    <section aria-label="area-panel" style={SECTION}>
      <h2 style={H2}>{t('quality.area.title')}</h2>
      {captured.length > 0 ? (
        <dl style={DL} data-testid="area-captured">
          {captured.map(([label, value]) => (
            <Fragment key={label}><dt style={DT}>{label}</dt><dd style={{ margin: 0 }}>{value}</dd></Fragment>
          ))}
        </dl>
      ) : <p data-testid="area-not-captured" style={MUTED}>{t('quality.area.notCaptured')}</p>}

      <h3 style={{ fontSize: 14, margin: '16px 0 6px' }}>{t('quality.area.quantity')}</h3>
      <JobQuantityPanel key={submission.vendorAssignmentId} fetchRow={fetchRow} notOpened={t('quality.area.quantityNotOpened')} assignmentId={submission.vendorAssignmentId} />
    </section>
  );
}
